import { Component, OnInit, AfterViewInit, OnDestroy, Input, Output, EventEmitter, ViewChild, ChangeDetectorRef, ElementRef, TemplateRef, EmbeddedViewRef } from '@angular/core';
import {FormControl, ValidatorFn, Validators} from '@angular/forms';
import { Overlay, OverlayRef, OverlayConfig } from '@angular/cdk/overlay';
import { TemplatePortalDirective } from '@angular/cdk/portal';

import { FieldComponent, UndoAction } from './field.component';
import { FormField, Link, Disclaimer } from './form-field.model';
import { DynamicOverlaysDirective } from './dynamic-overlays.directive';

@Component({

  selector: 'app-text-field',
  templateUrl: './text-field.component.html',
  styleUrls: ['./text-field.component.scss']
})
export class TextFieldComponent extends FieldComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() draftData: any;
  @Input() field: FormField;
  @Input() dynamicOverlays: DynamicOverlaysDirective;
  @Input() locked: boolean;
  @Input() lockValues: boolean = false;
  @Input() templateData: any;
  @Input() versionChanged: boolean = false;
  @Input() workflowId: number;
  @Output() focusChanged = new EventEmitter<boolean>();
  @Output() highlightChanged = new EventEmitter<{highlighted: boolean, color?: string}>();
  @Output() resized = new EventEmitter<FieldComponent>();
  @Output() returnKey = new EventEmitter<FieldComponent>();
  @Output() undoableAction = new EventEmitter<UndoAction>();
  @Output() updateLinked = new EventEmitter<{link: Link, value?: any, categories?: any[], enabled?: boolean}>();
  @Output() viewLoaded = new EventEmitter<FieldComponent>();
  @ViewChild("hintTemplate") hintTemplate: TemplatePortalDirective;
  @ViewChild("hintElement") hintElement: ElementRef;
  @ViewChild("versionTemplate") versionTemplate: TemplateRef<null>;
  @ViewChild("versionElement") versionElement: ElementRef;

  align: string;
  prefix: string;
  debounce: number = 0;
  defaultValue: string;
  disclaimer: Disclaimer;
  focused: boolean = false;
  id: string;
  highlightColor: string;
  highlightPattern: string;
  highlightValues: string[];
  highlighted: boolean = false;
  hint: string;
  hintOverlayRef: OverlayRef;
  label: string;
  links: Link[];
  maxLength: number;
  minLength: number;
  placeholder: string;
  pattern: string;
  previousValue: string;
  required: boolean = false;
  showingPreviousValue: boolean = false;
  suffix: string;
  value: string;
  versionView: EmbeddedViewRef<null>;
  get blank() { return this.value == null || this.value.trim() == '' }
  get touched(): boolean {return this.control != null && this.control.touched}

  constructor(
    cd: ChangeDetectorRef,
    private overlay: Overlay) { super(cd) }

  ngOnInit() {
    if (this.id == null) {this.id = this.field.id}
    if (this.pattern == null) {this.pattern = this.field.pattern}
    if (this.placeholder == null) {this.placeholder = this.field.placeholder}

    this.prefix = this.field.prefix;
    this.suffix = this.field.suffix;
    this.hint = this.field.hint;
    this.label = this.field.label;
    this.links = this.field.links;
    this.minLength = this.field.min;
    this.maxLength = this.field.max;
    this.required = this.field.required;
    this.align = this.field.align;
    this.highlightColor = this.field.highlightColor;
    this.highlightPattern = this.field.highlightPattern;
    this.highlightValues = this.field.highlightValues;
    if (this.locked == null) { this.locked = (this.field.locked === true) }

    this.loadDefaultValue();
    this.loadInitialState(this.loadValidators(), this.draftData);
  }

  ngAfterViewInit() {
    this.viewLoaded.emit(this);
  }

  ngOnDestroy() {
    this.hidePreviousValue();
  }

  loadTemplateValue(value) {
    let v = value instanceof Array ? value[0].stored : value;
    return v != null ? ''+v : null;
  }

  loadDefaultValue() {
    let templateValue: string;
    if (this.templateData != null) {
      templateValue = this.loadTemplateValue(this.templateData);
    }

    if (templateValue != null && templateValue.trim() != '') {
      this.defaultValue = templateValue
      if (this.lockValues) { this.locked = true };
    } else if (this.field.defaultValue != null) {
      let v = this.field.defaultValue.stored;
      this.defaultValue = v != null ? ''+v : null;
    }
  }

  loadInitialState(validators?: Array<ValidatorFn>, draftValue?: string) {
    this.value = draftValue != null ? this.loadTemplateValue(draftValue) : this.defaultValue
    this.control = new FormControl(this.value, validators);
    this.control.valueChanges.debounceTime(this.debounce).subscribe((value) => {
      this._dirty = this.initialized && !this.valuesEqual(value, this.value);
      this.value = value;
      this.valid = this.control.valid;
      this.emitValue();
      this.updateLinkedFields();
      this.cd.markForCheck();
      this.initialized = true;
    })

    // Emit initial validity and value
    this.validate();
    this.emitValue();
    if (this.locked) { this.disable(); }
  }

  loadValidators() {
    let validators = [];
    if (this.required === true) {validators.push(Validators.required)}
    if (this.pattern != null) {validators.push(Validators.pattern(this.pattern))}
    return validators;
  }

  validate() {
    if (this.control != null) {
      let disabled = this.control.disabled;
      if (disabled) { this.control.enable({emitEvent: false}); }
      this.control.updateValueAndValidity({emitEvent: false});
      this.valid = this.control.valid;
      if (disabled) { this.control.disable({emitEvent: false}); }
      this.cd.markForCheck();
    }
  }

  emitValue() {
    let value = !this.blank ? this.value : null;
    if (this.prefix != null && value != null) {
      value = this.prefix + value
    }
    this.valueChanged.emit({field: this.id, value: value});
    this.checkHighlight();
  }

  setValue(value: any, emit: boolean = true) {
    this.value = value;
    if (this.control != null) {
      this.control.setValue(value, {emitEvent: false});
    }
    this.validate();
    if (emit) { this.emitValue(); }
  }

  setPreviousValue(value) {
    this.previousValue = value;
    this.versionChanged = this.value !== this.previousValue;
    if (this.versionChanged) {
      this.showPreviousValue();
    }
    this.cd.markForCheck();
  }

  resetPreviousValue() {
    this.previousValue = null;
    this.versionChanged = false;
    this.hidePreviousValue();
    this.cd.markForCheck();
  }

  onKey(event): boolean {
    switch (event.keyCode) {
    case 13:
      return this.onReturnKey();
    default:
      return true;
    }
  }

  onReturnKey() {
    this.returnKey.emit(this);
    return false;
  }

  onFocus(event) {
    this.focused = true;
    this.focusChanged.emit(true);
    return true;
  }

  onBlur(event) {
    this.focused = false;
    this.focusChanged.emit(false);
    if (this.control != null && this.value != null) {
      this.value = this.value.trim()
      this.control.setValue(this.value, {emitEvent: false})
      this.validate();
      this.emitValue();
    }
    this.updateLinkedRows();
    return true;
  }

  reset() {
    this.loadInitialState();
    this.cd.markForCheck();
  }

  compare(other): number {
    let a = this.value != null ? this.value : '';
    let b = other.value != null ? other.value : '';
    if (a == '' && b != '') {
      return -1;
    } else if (b == '' && a != '') {
      return 1;
    } else {
      return a.localeCompare(b, 'us', {sensitivity: "base", ignorePunctuation: true});
    }
  }

  showHint() {
    let config = new OverlayConfig();
    config.positionStrategy = this.overlay.position().connectedTo(
      this.hintElement,
      {originX: 'end', originY: 'bottom'},
      {overlayX: 'start', overlayY: 'bottom'}
    );
    config.scrollStrategy = this.overlay.scrollStrategies.close();
    this.hintOverlayRef = this.overlay.create(config);
    this.hintOverlayRef.attach(this.hintTemplate);
  }

  hideHint() {
    if (this.hintOverlayRef != null) {this.hintOverlayRef.dispose()};
  }

  updateLinkedFields(userInitiated: boolean = false) {
    if (this.links != null) {
      let value = !this.blank ? this.value.trim() : null;
      if (value != null && this.prefix != null) {
        value = this.prefix + value;
      }
      for (let link of this.links) {
        if (!link.row) {
          this.updateLinked.emit({link: link, value: value})
        }
      }
    }
  }

  updateLinkedRows() {
    if (this.links != null) {
      let value = !this.blank ? this.value.trim() : null;
      if (value != null && this.prefix != null) {
        value = this.prefix + value;
      }
      for (let link of this.links) {
        if (link.row) {
          let enabled = false;
          if (link.presence) {
            enabled = (value != null)
          } else if (link.match && value != null) {
            enabled = (link.match.find((x) => x == value) != null)
          }
          this.updateLinked.emit({link: link, enabled: enabled})
        }
      }
    }
  }

  setCategories(category) { }

  checkHighlight() {
    let value = this.value != null ? this.value : '';
    if (this.highlightValues != null) {
      let match = false;
      for (let v of this.highlightValues) {
        if (v === value) {
          match = true;
          break;
        }
      }
      this.highlighted = match;
      this.highlightChanged.emit({highlighted: this.highlighted});
    } else if (this.highlightPattern != null) {
      let regex = new RegExp(this.highlightPattern);
      this.highlighted = regex.test(value);
      this.highlightChanged.emit({highlighted: this.highlighted});
    }
  }

  showPreviousValue() {
    this.showingPreviousValue = true;
    let container = this.dynamicOverlays.viewContainerRef;
    this.versionView = container.createEmbeddedView(this.versionTemplate);
    this.positionOverlays();
  }

  hidePreviousValue() {
    if (this.versionView != null) {
      this.versionView.destroy();
    }
    this.showingPreviousValue = false;
  }

  togglePreviousValue() {
    if (this.showingPreviousValue) {
      this.hidePreviousValue();
    } else {
      this.showPreviousValue();
    }
  }

  positionOverlays() {
    if (this.versionView != null) {
      let rect = this.versionElement.nativeElement.getBoundingClientRect();
      let rootNode = this.versionView.rootNodes.find((n) => n.tagName == 'DIV');
      rootNode.style.left = '' + (rect.left + window.pageXOffset) + 'px';
      rootNode.style.top = '' + (rect.top + window.pageYOffset) + 'px';
      rootNode.style.maxWidth = '' + rect.width + 'px';
    }
  }

  setPatternValue(field: string, value: any) { }

  undo(action: UndoAction) { }
}



// WEBPACK FOOTER //
// ./src/app/forms/text-field.component.ts